/* PresentationStudioModal — Studio de présentation (V1 : deals).
 * Flow : NOAM propose le plan → l'humain arbitre (inclure/exclure/réordonner)
 * → génération → HTML autoporteur téléchargeable / prévisualisable. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  X, Loader2, Sparkles, Download, Eye, ArrowUp, ArrowDown,
  Presentation, RefreshCw, CheckCircle2, Link2, Copy, Ban, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { NewLinkForm } from "@/pages/saas/DealLinks";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function SharePanel({ pres }) {
  const [share, setShare] = useState(null);
  const [accesses, setAccesses] = useState([]);
  const [url, setUrl] = useState("");
  const [expires, setExpires] = useState("0");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/workspace/studio/presentations/${pres.id}/share/log`)
      .then((r) => { setShare(r.data.share); setAccesses(r.data.accesses || []); })
      .catch(() => {});
  }, [pres.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/studio/presentations/${pres.id}/share`,
        { expires_days: Number(expires) || null });
      setUrl(`${API_BASE}${r.data.url_path}`);
      notify.success("Lien de partage créé.");
      load();
    } catch (e) { notify.error(fmtError(e, "Création impossible.")); }
    setBusy(false);
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/workspace/studio/presentations/${pres.id}/share`);
      setUrl("");
      notify.success("Lien révoqué immédiatement.");
      load();
    } catch (e) { notify.error(fmtError(e, "Révocation impossible.")); }
    setBusy(false);
  };
  const copy = () => { navigator.clipboard?.writeText(url); notify.success("Lien copié."); };
  const active = share?.enabled;
  const [dlRoom, setDlRoom] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);
  const createDealRoom = async () => {
    setDlBusy(true);
    try {
      const code = window.prompt("Nom de code du dossier (ex. Dossier OL-14) :", pres.plan?.title || "Dossier");
      if (!code) { setDlBusy(false); return; }
      const { data } = await api.post("/workspace/deal-links/rooms/from-studio",
        { presentation_id: pres.id, code_name: code });
      setDlRoom(data);
    } catch (e) { notify.error(fmtError(e, "Création du dossier impossible.")); }
    setDlBusy(false);
  };
  return (
    <div className="pt-3 border-t border-white/5 space-y-2" data-testid="studio-share-panel">
      <div className="noam-overline inline-flex items-center gap-1.5">
        <Link2 className="w-3 h-3" /> Lien de partage (snapshot publié, révocable)
      </div>
      {!active ? (
        <div className="flex items-center gap-2 flex-wrap">
          <select value={expires} onChange={(e) => setExpires(e.target.value)}
            className="noam-input text-[12px] py-1.5" data-testid="studio-share-expiry">
            <option value="0">Sans expiration</option>
            <option value="7">Expire dans 7 jours</option>
            <option value="30">Expire dans 30 jours</option>
            <option value="90">Expire dans 90 jours</option>
          </select>
          <button onClick={create} disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded-md border border-[#378ADD]/40 text-[#8FC1EE] hover:bg-[#378ADD]/10 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="studio-share-create-btn">
            <Link2 className="w-3 h-3" /> Créer le lien
          </button>
          {share?.revoked_at && <span className="text-[10px] text-zinc-600">Précédent lien révoqué le {new Date(share.revoked_at).toLocaleString("fr-CH")}</span>}
        </div>
      ) : (
        <div className="space-y-2">
          {url ? (
            <div className="flex items-center gap-2">
              <input readOnly value={url} className="noam-input flex-1 text-[11px] font-mono" data-testid="studio-share-url" onFocus={(e) => e.target.select()} />
              <button onClick={copy} className="p-1.5 rounded-md border border-white/10 text-zinc-400 hover:text-white" data-testid="studio-share-copy" title="Copier"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500">Lien actif (créé le {share?.created_at ? new Date(share.created_at).toLocaleString("fr-CH") : ""}). Le jeton complet n'est affiché qu'à la création.</div>
          )}
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-500">
            <span data-testid="studio-share-access-count">{share?.access_count || 0} consultation(s)</span>
            {share?.expires_at && <span>Expire le {new Date(share.expires_at).toLocaleDateString("fr-CH")}</span>}
            <button onClick={revoke} disabled={busy}
              className="inline-flex items-center gap-1 text-[#E24B4A] hover:underline"
              data-testid="studio-share-revoke-btn">
              <Ban className="w-3 h-3" /> Révoquer immédiatement
            </button>
          </div>
          {accesses.length > 0 && (
            <div className="max-h-24 overflow-y-auto space-y-0.5" data-testid="studio-share-log">
              {accesses.slice(0, 10).map((a) => (
                <div key={a.id} className="text-[10px] text-zinc-600 font-mono">
                  {new Date(a.at).toLocaleString("fr-CH")} · {a.ip}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Deal Link sécurisé — durée limitée, par destinataire (console : Origination → Liens de partage) */}
      <div className="pt-2 border-t border-white/5 space-y-2" data-testid="studio-deal-link-panel">
        <div className="noam-overline inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" /> Deal Link sécurisé (durée limitée, un lien par destinataire)
        </div>
        {!dlRoom ? (
          <button onClick={createDealRoom} disabled={dlBusy}
            className="text-[11px] px-3 py-1.5 rounded-md border border-[#B0872E]/40 text-[#E3C27E] hover:bg-[#B0872E]/10 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="studio-deal-link-create">
            {dlBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Créer un Deal Link depuis cette présentation
          </button>
        ) : (
          <>
            <NewLinkForm roomId={dlRoom.id} />
            <a href="/app/origination-hub?tab=liens" className="text-[10px] text-[#8FC1EE] hover:underline">Gérer tous les liens : Origination → Liens de partage</a>
          </>
        )}
      </div>
    </div>
  );
}

export default function PresentationStudioModal({ subject, onClose }) {
  const [pres, setPres] = useState(null);
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("noam");
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.get("/workspace/studio/presentations", { params: { subject_type: subject.type, subject_id: subject.id } })
      .then((r) => setHistory((r.data.items || []).filter((p) => p.status === "ready")))
      .catch(() => {});
  }, [subject.type, subject.id]);

  const poll = useCallback((pid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/workspace/studio/presentations/${pid}`);
        setPres(r.data);
        if (r.data.plan?.title) setTitle((t) => t || r.data.plan.title);
        if (!["proposing", "generating"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          if (r.data.status === "failed") notify.error(r.data.error || "Échec du Studio.");
          if (r.data.status === "ready") { notify.success("Présentation générée."); loadHistory(); }
        }
      } catch { /* retry */ }
    }, 2500);
  }, [loadHistory]);

  const propose = useCallback(async () => {
    try {
      const r = await api.post("/workspace/studio/presentations/propose",
        { subject_type: subject.type, subject_id: subject.id });
      setPres({ id: r.data.presentation_id, status: "proposing" });
      poll(r.data.presentation_id);
    } catch (e) { notify.error(fmtError(e, "Proposition impossible.")); }
  }, [subject.type, subject.id, poll]);

  useEffect(() => { propose(); loadHistory(); return () => clearInterval(pollRef.current); }, [propose, loadHistory]);

  const patchSections = async (sections) => {
    try {
      const r = await api.patch(`/workspace/studio/presentations/${pres.id}/plan`, { sections, title });
      setPres((p) => ({ ...p, plan: r.data.plan }));
    } catch (e) { notify.error(fmtError(e, "Mise à jour impossible.")); }
  };
  const toggle = (s) => patchSections([{ id: s.id, included: !s.included }]);
  const move = (idx, dir) => {
    const secs = [...(pres.plan?.sections || [])];
    const j = idx + dir;
    if (j < 0 || j >= secs.length) return;
    [secs[idx], secs[j]] = [secs[j], secs[idx]];
    patchSections(secs.map((s, i) => ({ id: s.id, order: i })));
  };
  const generate = async () => {
    try {
      if (title && title !== pres.plan?.title) {
        await api.patch(`/workspace/studio/presentations/${pres.id}/plan`, { title, sections: [] });
      }
      await api.post(`/workspace/studio/presentations/${pres.id}/generate`, { theme });
      setPres((p) => ({ ...p, status: "generating" }));
      poll(pres.id);
    } catch (e) { notify.error(fmtError(e, "Génération impossible.")); }
  };

  const plan = pres?.plan;
  const includedCount = (plan?.sections || []).filter((s) => s.included).length;
  const busy = ["proposing", "generating"].includes(pres?.status);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card max-w-2xl w-full max-h-[88vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="studio-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="noam-overline inline-flex items-center gap-1.5">
              <Presentation className="w-3.5 h-3.5 text-[#7F77DD]" /> Studio de présentation
            </div>
            <div className="text-sm font-semibold text-zinc-100 mt-0.5">{subject.label}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="studio-close"><X className="w-4 h-4" /></button>
        </div>

        {pres?.status === "proposing" && (
          <div className="py-10 text-center text-zinc-400 text-sm" data-testid="studio-proposing">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2 text-[#7F77DD]" />
            NOAM compose la présentation depuis les données réelles du dossier…
          </div>
        )}

        {pres?.status === "failed" && (
          <div className="py-6 text-center space-y-3">
            <div className="text-[#E24B4A] text-sm">{pres.error || "Échec."}</div>
            <button onClick={propose} className="btn-primary text-[12px] inline-flex items-center gap-1.5" data-testid="studio-retry">
              <RefreshCw className="w-3.5 h-3.5" /> Réessayer
            </button>
          </div>
        )}

        {plan && pres.status !== "proposing" && (
          <>
            {plan.recommendation && (
              <div className="text-[12px] text-zinc-400 border-l-2 border-[#7F77DD]/50 pl-3" data-testid="studio-recommendation">
                {plan.recommendation}
              </div>
            )}
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="noam-input w-full font-semibold" placeholder="Titre du document"
              data-testid="studio-title-input" disabled={busy} />
            {plan.subtitle && <div className="text-[12px] text-zinc-500 -mt-2">{plan.subtitle}</div>}

            <div className="space-y-1.5" data-testid="studio-sections">
              {(plan.sections || []).map((s, i) => (
                <div key={s.id}
                  className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border transition-colors ${s.included ? "border-white/10 bg-white/[0.02]" : "border-white/5 opacity-45"}`}
                  data-testid={`studio-section-${s.id}`}>
                  <input type="checkbox" checked={!!s.included} onChange={() => toggle(s)} disabled={busy}
                    className="mt-1 accent-[#7F77DD]" data-testid={`studio-section-toggle-${s.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] text-zinc-100 font-medium">{i + 1}. {s.title}</div>
                    {s.rationale && <div className="text-[11px] text-zinc-500">{s.rationale}</div>}
                  </div>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button onClick={() => move(i, -1)} disabled={busy || i === 0} className="text-zinc-600 hover:text-white disabled:opacity-30" data-testid={`studio-section-up-${s.id}`}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => move(i, 1)} disabled={busy || i === (plan.sections || []).length - 1} className="text-zinc-600 hover:text-white disabled:opacity-30" data-testid={`studio-section-down-${s.id}`}><ArrowDown className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5" data-testid="studio-theme-picker">
                <span className="text-[11px] text-zinc-500 mr-1">Thème :</span>
                {[["noam", "NOAM sombre"], ["institutionnel", "Institutionnel"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTheme(k)} disabled={busy}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${theme === k ? "border-[#B0872E] text-[#E3C27E] bg-[#B0872E]/10 font-semibold" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
                    data-testid={`studio-theme-${k}`}>{l}</button>
                ))}
              </div>
              <span className="text-[11px] text-zinc-500">
                {includedCount} section(s) incluse(s) · statuts épistémiques préservés dans le rendu
              </span>
              <div className="flex items-center gap-2">
                <button onClick={propose} disabled={busy}
                  className="text-[11px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-400 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="studio-replan-btn">
                  <RefreshCw className="w-3 h-3" /> Reproposer
                </button>
                <button onClick={generate} disabled={busy || !includedCount}
                  className="btn-primary text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="studio-generate-btn">
                  {pres.status === "generating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {pres.status === "ready" ? "Régénérer" : "Générer la présentation"}
                </button>
              </div>
            </div>
          </>
        )}

        {pres?.status === "generating" && (
          <div className="text-center text-zinc-400 text-[12px] py-2" data-testid="studio-generating">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2 text-[#7F77DD]" />
            NOAM rédige et met en page le document (30-90 s)…
          </div>
        )}

        {pres?.status === "ready" && (
          <>
            {pres.stale && (
              <div className="rounded-lg border border-[#EF9F27]/40 bg-[#EF9F27]/[0.07] px-4 py-2.5 text-[12px] text-[#EF9F27] inline-flex items-center gap-2 w-full" data-testid="studio-stale-banner">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Les données ont changé depuis la génération ({(pres.stale_reasons || []).join(", ") || "objet modifié"}) — pensez à régénérer.
              </div>
            )}
            <div className="rounded-lg border border-[#1D9E75]/30 bg-[#1D9E75]/[0.06] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="studio-ready">
            <span className="text-[12px] text-[#1D9E75] inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Prêt — HTML autoporteur ({Math.round((pres.html_size || 0) / 1024)} Ko), envoyable tel quel.
            </span>
            <div className="flex items-center gap-2">
              <a href={`${API_BASE}/api/workspace/studio/presentations/${pres.id}/preview`} target="_blank" rel="noreferrer"
                className="text-[11px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:text-white inline-flex items-center gap-1.5"
                data-testid="studio-preview-btn">
                <Eye className="w-3.5 h-3.5" /> Aperçu
              </a>
              <a href={`${API_BASE}/api/workspace/studio/presentations/${pres.id}/download`}
                className="btn-primary text-[12px] inline-flex items-center gap-1.5" data-testid="studio-download-btn">
                <Download className="w-3.5 h-3.5" /> Télécharger .html
              </a>
            </div>
            </div>
            <SharePanel pres={pres} />
          </>
        )}

        {history.length > 0 && (
          <div className="pt-2 border-t border-white/5">
            <div className="noam-overline mb-1.5">Versions précédentes</div>
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-[11px] text-zinc-500 py-1" data-testid={`studio-history-${h.id}`}>
                <span className="truncate inline-flex items-center gap-1.5">
                  {h.stale && <AlertTriangle className="w-3 h-3 text-[#EF9F27] shrink-0" title={`Obsolète : ${(h.stale_reasons || []).join(", ")}`} />}
                  {h.plan?.title || h.subject_label} · {new Date(h.generated_at || h.created_at).toLocaleString("fr-CH")}
                </span>
                <a href={`${API_BASE}/api/workspace/studio/presentations/${h.id}/download`}
                  className="text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid={`studio-history-dl-${h.id}`}>
                  <Download className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
